import {
  Gym, GymEnvironment, GymTimeZone, GymTypes,
  ILogger, Organisation, TimeZoneMapping,
} from 'fitbeat.models';
import { getAreaCategoriesForEachZoneScreenInGym } from '../../../config/zone-mappings';
import { loadTheHardcodedStaticGymConfig } from '../../../config/gymconfig';
import { GymInfoForTimeslots, UserProfile } from '../../../entities';
import { IPersistedGymConfig } from '../../../entities/gym/IPersistedGymConfig';

interface IDummyPlanId {
  priceId: string;
  accountId: string;
}

type PouchDB = any; // types not setup in gym core, should be: PouchDB.Database

interface IInitializeOptions {
  logger: ILogger;
  generalDb: PouchDB;
  environment: GymEnvironment;
}

interface IPartialGymConfigInDb {
  [gymName: string]: IPersistedGymConfig | undefined;
}

type RawGymConfigs = Gym & IPersistedGymConfig;

/**
 * The raw gym config, which was loaded from the hardcoded configs in gymconfig.ts, merged with the partially
 * persisted gym details from PARTIAL_GYM_CONFIG
 * This should only be used to build the caching of the (full) gym details
 * Currently updated/added to from tests, but the persisted fields should only be set in the db
 */
let gymConfig: { [key: string]: RawGymConfigs } = {};

const singleDocumentId = 'PARTIAL_GYM_CONFIG';

async function getOrCreatePartialConfigsFromDb(logger: ILogger, generalDb: PouchDB): Promise<IPartialGymConfigInDb> {
  try {
    const dbGymConfigDoc: IPartialGymConfigInDb = await generalDb.get(singleDocumentId);
    delete dbGymConfigDoc._id;
    delete dbGymConfigDoc._rev;
    return dbGymConfigDoc;
  } catch (e) {
    if (e.status === 404) {
      logger.warn(`creating empty document ${singleDocumentId} for partial gym configs in db`);
      await generalDb.put({
        _id: singleDocumentId,
      });
      return {};
    }

    throw e;
  }
}

export async function updateGymInPartialDbForTests(
  generalDb: PouchDB,
  gymName: string,
  data: Partial<IPersistedGymConfig>,
) {
  // this function can eventually become the bases for updating gym configs from ex business tools
  async function getOrCreateEmptyDocument() {
    try {
      const saved = await generalDb.get(singleDocumentId);
      return saved;
    } catch (e) {
      return { _id: singleDocumentId };
    }
  }

  const doc = await getOrCreateEmptyDocument();
  doc[gymName] = {
    ...doc[gymName],
    ...data,
  };
  await generalDb.put(doc);
}

export class DbGymRepository {
  public static async initialize(options?: IInitializeOptions) {
    gymConfig = loadTheHardcodedStaticGymConfig(options?.environment);

    // TODO: #2426: DbGymRepository is still being used in some front ends
    // they wont initialize from db, so hardcoded gymconfig still needs to contain most fields
    if (!options) {
      DbGymRepository.initializeCachedGyms();
      return;
    }
    await DbGymRepository.initializeFromDbAndUpdateCache(options);
  }

  public static async initializeFromDbAndUpdateCache(options: IInitializeOptions) {
    await DbGymRepository.mergeHardcodedWithPartialDbConfigs(options.logger, options.generalDb);
    // TODO: #2426: load the whole config from db
    DbGymRepository.initializeCachedGyms();
    options.logger.info(`DbGymRepository initialized, ${DbGymRepository.getAllSync().length} total gyms`);
  }

  public static getAllDummyPlanIdsForSignup(): IDummyPlanId[] {
    const pricesSet: IDummyPlanId[] = [];
    for (const gym of DbGymRepository.getAllSync()) {
      if (gym.dummyPriceIdForSignup) {
        pricesSet.push({
          priceId: gym.dummyPriceIdForSignup,
          accountId: gym.accountId,
        });
      }
    }

    return pricesSet;
  }

  public static getByName(name: string): GymInfoForTimeslots {
    const cached = DbGymRepository.cache.get(name);
    if (cached != null) {
      return cached;
    }
    throw new Error(`No mapping for gym ${name}`);
  }

  public static getGymInfoForTimeslots(gym: Gym): GymInfoForTimeslots {
    function canCast(t: Gym): t is GymInfoForTimeslots {
      return (t as GymInfoForTimeslots).specificOperatingTimes !== undefined;
    }

    if (canCast(gym)) {
      return gym;
    }
    return DbGymRepository.getByName(gym.name);
  }

  public static getByAccountId(accountId: string, gymEnvironment: GymEnvironment): Gym {
    const gym: Gym | undefined = DbGymRepository.getAllSync()
      .find((configGym) => configGym.accountId === accountId && configGym.environment === gymEnvironment);
    if (!gym) {
      throw new Error(`No mapping for gym accountId: ${accountId}`);
    }
    return gym;
  }

  public static getByNameWithTimezone(name: string, timezone: GymTimeZone): GymInfoForTimeslots {
    const gym = DbGymRepository.getByName(name);
    if (gym.type !== GymTypes.Home) {
      throw new Error(`Cannot set timezone ${timezone} on gym ${gym.name} ${gym.type}`);
    }

    return new GymInfoForTimeslots({
      ...gym,
      timezone,
    });
  }

  public static getGymNames(type: GymTypes): string[] {
    const gyms: Gym[] = DbGymRepository.getAllSync();
    const gymsOfType: Gym[] = gyms.filter((gym) => gym.type === type);
    return gymsOfType.map((gym) => gym.name);
  }

  public static getTestGym() {
    return DbGymRepository.getByName(process.env.GYM_NAME || 'Centurion');
  }

  public static getTestHomeGym() {
    return DbGymRepository.getByName(process.env.HOME_GYM_NAME || 'HomeSA');
  }

  public static async createTestGym(
    name: string,
    type: GymTypes,
    zoneMapping: number[][],
    allowedCapacity = 1,
    timezone: GymTimeZone = TimeZoneMapping.getSystemTimeZoneName(),
    organisation?: Organisation,
    autoCheckin?: boolean,
  ): Promise<Gym> {
    const numOfZones: number = zoneMapping.length;
    const gym = new Gym({
      name,
      type,
      numOfZones,
      lastUpdated: new Date(),
      environment: GymEnvironment.Staging,
      displayName: name,
      zoneMapping,
      publicHolidays: [],
      allowedCapacityAsPercentOfFullGym: allowedCapacity,
      timezone,
      organisation,
      autoCheckin,
    });
    gymConfig[name] = gym;
    DbGymRepository.createCachedGym(gym);
    return gym;
  }

  public static updateGym(name: string, gymOptions: Partial<RawGymConfigs>) {
    // RawGymConfigs is used as a type, but dont allow any of the fields from the tests
    if (gymOptions.specificTimeSlotTypes) {
      throw new Error('The `specificTimeSlotTypes` field should be set in the db, even in tests!');
    }
    if (gymOptions.cancelledDays) {
      throw new Error('The `cancelledDays` field should be set in the db, even in tests!');
    }
    if (gymOptions.timeSlotTypes) {
      throw new Error('The `timeSlotTypes` field should be set in the db, even in tests!');
    }
    if (gymOptions.enableOnDemandStudioBookings) {
      throw new Error('The `enableOnDemandStudioBookings` field should be set in the db, even in tests!');
    }
    if (gymOptions.overridenAllowedBookingTimeFrames) {
      throw new Error('The `overridenAllowedBookingTimeFrames` field should be set in the db, even in tests!');
    }
    if (gymOptions.officeGymConfig) {
      throw new Error('The `officeScheduleConfig` field should be set in the db, even in tests!');
    }
    const mappedGym = gymConfig[name];
    gymConfig[name] = new Gym({ ...mappedGym, ...gymOptions });
    DbGymRepository.createCachedGym(gymConfig[name]);
  }

  public static async getGyms(environment: GymEnvironment, type?: GymTypes): Promise<Gym[]> {
    const allGyms: Gym[] = DbGymRepository.getAllSync();

    const filterEnvironment = (gym: Gym) => gym.environment === environment;

    const filterEnabled = (gym: Gym) => gym.enabled;

    const filterGymTypes = (gym: Gym) => !type || gym.type === type;

    return allGyms
      .filter(filterEnabled)
      .filter(filterGymTypes)
      .filter(filterEnvironment);
  }

  public static async getAllGyms(environment: GymEnvironment): Promise<Gym[]> {
    return Promise.resolve(DbGymRepository.getGyms(environment));
  }

  public static async getGymsFor(
    userProfile: UserProfile,
    environment: GymEnvironment,
    canAccessAllStudioGymsInOrg: boolean,
    onlyLegacy = false,
  )
    : Promise<Gym[]> {
    const basicFilteredGyms: Gym[] = await DbGymRepository.getGyms(environment);
    const defaultGym = basicFilteredGyms.find(
      (singleGym) => userProfile.gymName === singleGym.name,
    );
    const studioGymFilter = (gym: Gym) => {
      if (canAccessAllStudioGymsInOrg) {
        return defaultGym?.organisation === gym.organisation;
      }
      return gym.name === defaultGym?.name;
    };

    const filterProductSpecificGyms = (gym: Gym) => {
      if (userProfile.has24HourAccess) {
        return true;
      }
      if (gym.type === GymTypes.Home) {
        // home is always available, irrespective of plan
        return true;
      }
      return studioGymFilter(gym);
    };

    const filterOnlyLegacyGyms = (gym: Gym) => {
      const lastLegacyGymCreateDate = new Date('2023-07-15T00:00:00.000Z');
      const isLegacyGym = gym.created < lastLegacyGymCreateDate;
      if (onlyLegacy && !isLegacyGym) {
        return false;
      }
      return true;
    };

    const gymPrioritySorting = (gymA: Gym, gymB: Gym) => {
      if (gymA.name === defaultGym?.name && gymB.name !== defaultGym?.name) {
        return -1;
      }
      if (gymB.name === defaultGym?.name && gymA.name !== defaultGym?.name) {
        return 1;
      }
      if (gymA.type === GymTypes.Indoor && gymB.type !== GymTypes.Indoor) {
        return -1;
      }
      if (gymB.type === GymTypes.Indoor && gymA.type !== GymTypes.Indoor) {
        return 1;
      }
      if (gymA.type === GymTypes.Outdoor && gymB.type !== GymTypes.Outdoor) {
        return -1;
      }
      if (gymB.type === GymTypes.Outdoor && gymA.type !== GymTypes.Outdoor) {
        return 1;
      }

      if (gymA.type === gymB.type) {
        return gymA.displayName.localeCompare(gymB.displayName);
      }

      return 0;
    };

    return Promise.resolve(basicFilteredGyms
      .filter(filterProductSpecificGyms)
      .filter(filterOnlyLegacyGyms)
      .sort(gymPrioritySorting));
  }

  public static getAllGymTimezones(): string[] {
    const gyms = DbGymRepository.getAllSync();
    return Array.from(new Set(gyms.map(({ timezone }) => timezone)));
  }

  public static getGymByPayfastMerchantId(merchantId: string): GymInfoForTimeslots | undefined {
    return DbGymRepository.getAllSync().find((gym) => gym.integrationConfig?.payfast?.merchantId === merchantId);
  }

  private static cache = new Map<string, GymInfoForTimeslots>();

  private static getAllSync(): GymInfoForTimeslots[] {
    const iterable = DbGymRepository.cache.values();
    return [...iterable];
  }

  private static getAllRawConfigSync(): RawGymConfigs[] {
    return Object.values(gymConfig);
  }

  private static async mergeHardcodedWithPartialDbConfigs(logger: ILogger, generalDb: PouchDB) {
    const persistedPartialGymConfigs: IPartialGymConfigInDb = await getOrCreatePartialConfigsFromDb(logger, generalDb);
    const hardcoded = DbGymRepository.getAllRawConfigSync();
    logger.info(`merging gym configs ${JSON.stringify({
      persisted: Object.keys(persistedPartialGymConfigs),
      hardcoded: hardcoded.map((g) => g.name),
    })}`);
    for (const gym of hardcoded) {
      const partialFromDb = persistedPartialGymConfigs[gym.name];
      if (partialFromDb) {
        gym.specificTimeSlotTypes = partialFromDb.specificTimeSlotTypes;
        gym.cancelledDays = partialFromDb.cancelledDays;
        gym.timeSlotTypes = partialFromDb.timeSlotTypes;
        gym.integrationConfig = partialFromDb.integrationConfig;
      }
      gym.enableOnDemandStudioBookings = partialFromDb?.enableOnDemandStudioBookings || 'Always';
      gym.companies = partialFromDb?.companies || [];
      gym.overridenAllowedBookingTimeFrames = partialFromDb?.overridenAllowedBookingTimeFrames;
      gym.officeGymConfig = partialFromDb?.officeGymConfig;
    }
  }

  private static initializeCachedGyms() {
    DbGymRepository.cache.clear();
    for (const gym of DbGymRepository.getAllRawConfigSync()) {
      DbGymRepository.createCachedGym(gym);
    }
  }

  private static createCachedGym(mappedGym: RawGymConfigs): GymInfoForTimeslots {
    const gym = new GymInfoForTimeslots(mappedGym);
    // do some basic data validation. rather crash on platform startup than during run
    getAreaCategoriesForEachZoneScreenInGym(gym.zoneMapping);

    DbGymRepository.cache.set(gym.name, gym);
    return gym;
  }
}
